document.addEventListener('DOMContentLoaded', function() {
  /**
   * SlideVideo component
   */

  var SlideVideoContainer = class extends HTMLElement {
    connectedCallback() {
      this.xSlideshowCarousel = this.closest('slideshow-carousel');
      this.autoplayInterval = null;
      this.autoplayTimeoutId = null;
      
      if (this.xSlideshowCarousel) {
        // Only initialize custom autoplay if data-autoplay is present (indicating videos exist)
        // or if video autoplay is enabled on a video in this container
        const dataAutoplay = this.xSlideshowCarousel.getAttribute('data-autoplay');
        if (dataAutoplay) {
          this.autoplayInterval = parseInt(dataAutoplay) * 1000; // Convert to milliseconds
        }

        if (dataAutoplay || this.querySelector('video[autoplay]')) {
          this.onDemandLoadVideos();
          this.handleSlideChange();
          this.xSlideshowCarousel.addEventListener('carousel:select', this.handleSlideChange.bind(this));
        }
      }
    }

    /**
     * Clear any existing autoplay timeout
     */
    clearAutoplayTimeout() {
      if (this.autoplayTimeoutId) {
        clearTimeout(this.autoplayTimeoutId);
        this.autoplayTimeoutId = null;
      }
    }

    /**
     * Start autoplay timer for image slides
     */
    startImageAutoplay() {
      if (!this.autoplayInterval) return;
      
      this.clearAutoplayTimeout();
      this.autoplayTimeoutId = setTimeout(() => {
        this.xSlideshowCarousel._dispatchEvent('control:next', this.xSlideshowCarousel._selectedIndex + 1);
      }, this.autoplayInterval);
    }
    
    /**
     * Handle nextSlide trigger when video ends
     * @param {VideoElement} video 
     * @returns void
     */
    videoPlaybackListener(video) {
      if (!video) return;

      // onended replaces any previous handler (avoids stacked next calls)
      video.onended = () => {
        this.xSlideshowCarousel._dispatchEvent('control:next', this.xSlideshowCarousel._selectedIndex + 1);
      };
    }

    /**
     * Get the currently visible video element
     * @param {Element} slide 
     * @returns {VideoElement|null}
     */
    getVisibleVideo(slide) {
      const videos = slide?.querySelectorAll('video');
      if (!videos || videos.length === 0) return null;
      
      // If only one video, return it
      if (videos.length === 1) return videos[0];
      
      // Find the visible video (not hidden by CSS)
      for (const video of videos) {
        const styles = window.getComputedStyle(video);
        if (styles.display !== 'none') {
          return video;
        }
      }
      
      // Fallback to first video
      return videos[0];
    }

    /**
     * Handle slide changes - play videos or start image autoplay
     * @param {*} event  
     */
    handleSlideChange(event) {
      // Clear any existing autoplay timeout
      this.clearAutoplayTimeout();
      
      const selectedSlide = event?.detail?.slide ?? this.xSlideshowCarousel.selectedSlide;
      const parentSlide = this.closest('.slideshow__slide');
      const selectedVideo = this.getVisibleVideo(selectedSlide);

      // Only the selected slide's video container should drive playback
      if (selectedVideo && parentSlide !== selectedSlide) return;
      // Only one container should drive the timer on image slides
      if (!selectedVideo && this !== this.xSlideshowCarousel.querySelector('slide-video-container')) return;
      
      if (selectedVideo?.hasAttribute('autoplay')) {
        // This is a video slide with autoplay enabled
        if (selectedVideo.readyState >= 3) {
          selectedVideo.play();
        } else {
          selectedVideo.addEventListener('canplay', function() {
            this.play();
          });
        }
        // Only advance when auto-rotate is on
        if (this.autoplayInterval) {
          this.videoPlaybackListener(selectedVideo);
        }
      } else {
        // This is an image slide (or video without autoplay) - start autoplay timer if we have an interval
        this.startImageAutoplay();
      }
    }

    /**
     * Generic lazyloading of slideshow videos
     */
    onDemandLoadVideos() {
      const parentElement = this.closest('.slideshow__slide');
      const videoSources = this.querySelectorAll('source[data-src]');
      const videoElements = this.querySelectorAll('video');

      const loadVideo = () => {
        videoSources.forEach(source => { source.src = source.dataset.src; });
        videoElements.forEach(video => { video.load(); });
      };

      const clearVideo = () => {
        videoElements.forEach(video => {
          video.onended = null;
          video.pause();
        });
        videoSources.forEach(source => { source.src = '' });
        videoElements.forEach(video => { video.load() });
      };

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'class') {
            if (parentElement.classList.contains('is-selected')) {
              loadVideo();
            } else {
              clearVideo();
            }
          }
        });
      });

      observer.observe(parentElement, { attributes: true });

      if (parentElement.classList.contains('is-selected')) {
        loadVideo();
      }
    }

    /**
     * Cleanup when component is disconnected
     */
    disconnectedCallback() {
      this.clearAutoplayTimeout();
    }
  };
  
  if (!window.customElements.get('slide-video-container')) {
    window.customElements.define('slide-video-container', SlideVideoContainer);
  }
});
